import React, { useState, useEffect, useMemo } from 'react';

const API = 'https://api.glowytics.com/api/pinterest';
const hdrs = () => ({
  'Content-Type': 'application/json',
  'X-Brand-ID': localStorage.getItem('currentBrandId') || '',
});
const opts = () => ({ credentials: 'include', headers: hdrs() });

const fmtNum = v => Number(v || 0).toLocaleString('en-US');
const fmtMoney = v => `$${Number(v || 0).toFixed(2)}`;
const fmtPct = v => `${(Number(v || 0) * 100).toFixed(2)}%`;

export default function PinterestTest() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState({});
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [accountAnalytics, setAccountAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [adGroups, setAdGroups] = useState(null);
  const [adGroupAnalytics, setAdGroupAnalytics] = useState(null);
  const [ads, setAds] = useState(null);
  const [adAnalytics, setAdAnalytics] = useState(null);
  const [customEndpoint, setCustomEndpoint] = useState('');
  const [customResult, setCustomResult] = useState(null);

  useEffect(() => { loadConnection(); }, []);

  const load = async (key, fn) => {
    setLoading(p => ({ ...p, [key]: true }));
    try { await fn(); } catch (e) { console.error(key, e); }
    setLoading(p => ({ ...p, [key]: false }));
  };

  const loadConnection = async () => {
    try {
      const r = await fetch(`${API}/connection`, opts());
      const d = await r.json();
      setConn(d);
    } catch (e) { console.error(e); }
  };

  const qs = `startDate=${startDate}&endDate=${endDate}`;

  const loadAccountAnalytics = () => load('accountAnalytics', async () => {
    const r = await fetch(`${API}/analytics?${qs}`, opts());
    setAccountAnalytics(await r.json());
  });

  const loadCampaigns = () => load('campaigns', async () => {
    const r = await fetch(`${API}/campaigns`, opts());
    setCampaigns(await r.json());
  });

  const loadCampaignAnalytics = () => load('campaignAnalytics', async () => {
    const r = await fetch(`${API}/campaigns/analytics?${qs}`, opts());
    setCampaignAnalytics(await r.json());
  });

  const loadAdGroups = () => load('adGroups', async () => {
    const r = await fetch(`${API}/ad-groups`, opts());
    setAdGroups(await r.json());
  });

  const loadAdGroupAnalytics = () => load('adGroupAnalytics', async () => {
    const r = await fetch(`${API}/ad-groups/analytics?${qs}`, opts());
    setAdGroupAnalytics(await r.json());
  });

  const loadAds = () => load('ads', async () => {
    const r = await fetch(`${API}/ads`, opts());
    setAds(await r.json());
  });

  const loadAdAnalytics = () => load('adAnalytics', async () => {
    const r = await fetch(`${API}/ads/analytics?${qs}`, opts());
    setAdAnalytics(await r.json());
  });

  const loadCustom = () => load('custom', async () => {
    if (!customEndpoint.trim()) return;
    const r = await fetch(`${API}/proxy?path=${encodeURIComponent(customEndpoint.trim())}`, opts());
    setCustomResult(await r.json());
  });

  const loadAll = async () => {
    await loadAccountAnalytics();
    await loadCampaigns();
    await loadCampaignAnalytics();
    await loadAdGroups();
    await loadAdGroupAnalytics();
    await loadAds();
    await loadAdAnalytics();
  };

  // Aggregate helpers
  const aggregateRows = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return null;
    return rows.reduce((acc, r) => {
      acc.spend += Number(r.SPEND_IN_DOLLAR || 0);
      acc.impressions += Number(r.IMPRESSION_1 || 0);
      acc.clicks += Number(r.CLICKTHROUGH_1 || 0);
      acc.conversions += Number(r.TOTAL_CONVERSIONS || 0);
      acc.convValue += Number(r.TOTAL_CONVERSIONS_VALUE || 0);
      acc.convQty += Number(r.TOTAL_CONVERSIONS_QUANTITY || 0);
      acc.count++;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, conversions: 0, convValue: 0, convQty: 0, count: 0 });
  };

  // Group campaign analytics by campaign
  const campaignSummary = useMemo(() => {
    if (!Array.isArray(campaignAnalytics)) return [];
    const map = {};
    campaignAnalytics.forEach(row => {
      const id = row.CAMPAIGN_ID || 'unknown';
      if (!map[id]) map[id] = { id, name: row.CAMPAIGN_NAME || id, spend: 0, impressions: 0, clicks: 0, conversions: 0, convValue: 0 };
      map[id].spend += Number(row.SPEND_IN_DOLLAR || 0);
      map[id].impressions += Number(row.IMPRESSION_1 || 0);
      map[id].clicks += Number(row.CLICKTHROUGH_1 || 0);
      map[id].conversions += Number(row.TOTAL_CONVERSIONS || 0);
      map[id].convValue += Number(row.TOTAL_CONVERSIONS_VALUE || 0);
    });
    return Object.values(map).sort((a, b) => b.spend - a.spend);
  }, [campaignAnalytics]);

  const adGroupSummary = useMemo(() => {
    if (!Array.isArray(adGroupAnalytics)) return [];
    const map = {};
    adGroupAnalytics.forEach(row => {
      const id = row.AD_GROUP_ID || 'unknown';
      if (!map[id]) map[id] = { id, name: row.AD_GROUP_NAME || id, spend: 0, impressions: 0, clicks: 0, conversions: 0, convValue: 0 };
      map[id].spend += Number(row.SPEND_IN_DOLLAR || 0);
      map[id].impressions += Number(row.IMPRESSION_1 || 0);
      map[id].clicks += Number(row.CLICKTHROUGH_1 || 0);
      map[id].conversions += Number(row.TOTAL_CONVERSIONS || 0);
      map[id].convValue += Number(row.TOTAL_CONVERSIONS_VALUE || 0);
    });
    return Object.values(map).sort((a, b) => b.spend - a.spend);
  }, [adGroupAnalytics]);

  const adSummary = useMemo(() => {
    if (!Array.isArray(adAnalytics)) return [];
    const map = {};
    adAnalytics.forEach(row => {
      const id = row.AD_ID || 'unknown';
      if (!map[id]) map[id] = { id, name: row.AD_NAME || id, campaignId: row.CAMPAIGN_ID, adGroupId: row.AD_GROUP_ID, spend: 0, impressions: 0, clicks: 0, conversions: 0, convValue: 0 };
      map[id].spend += Number(row.SPEND_IN_DOLLAR || 0);
      map[id].impressions += Number(row.IMPRESSION_1 || 0);
      map[id].clicks += Number(row.CLICKTHROUGH_1 || 0);
      map[id].conversions += Number(row.TOTAL_CONVERSIONS || 0);
      map[id].convValue += Number(row.TOTAL_CONVERSIONS_VALUE || 0);
    });
    return Object.values(map).sort((a, b) => b.spend - a.spend);
  }, [adAnalytics]);

  const Section = ({ title, badge, children }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <span className="font-semibold text-gray-800">{title}</span>
        {badge && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{badge}</span>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  const Btn = ({ onClick, loading: l, children, variant = 'primary' }) => {
    const cls = variant === 'primary'
      ? 'bg-blue-600 hover:bg-blue-700 text-white'
      : variant === 'green'
        ? 'bg-green-600 hover:bg-green-700 text-white'
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700';
    return (
      <button onClick={onClick} disabled={l} className={`px-4 py-2 rounded-lg font-medium text-sm ${cls} disabled:opacity-50 transition-colors`}>
        {l ? 'Loading...' : children}
      </button>
    );
  };

  const JsonBlock = ({ data }) => (
    <details className="mt-2">
      <summary className="text-xs text-gray-500 cursor-pointer hover:text-blue-600">Raw JSON ({Array.isArray(data) ? data.length + ' rows' : 'object'})</summary>
      <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap mt-1">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );

  const KpiCards = ({ data }) => {
    if (!data) return null;
    const cards = [
      { label: 'Spend', value: fmtMoney(data.spend), color: 'red' },
      { label: 'Impressions', value: fmtNum(data.impressions), color: 'blue' },
      { label: 'Clicks', value: fmtNum(data.clicks), color: 'green' },
      { label: 'CTR', value: data.impressions > 0 ? fmtPct(data.clicks / data.impressions) : '0%', color: 'purple' },
      { label: 'CPC', value: data.clicks > 0 ? fmtMoney(data.spend / data.clicks) : '$0.00', color: 'orange' },
      { label: 'Conversions', value: fmtNum(data.conversions), color: 'teal' },
      { label: 'Conv. Value', value: fmtMoney(data.convValue), color: 'indigo' },
      { label: 'Days', value: data.count, color: 'gray' },
    ];
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
        {cards.map(c => (
          <div key={c.label} className={`bg-${c.color}-50 rounded-lg p-3 text-center`}>
            <div className="text-xs text-gray-500">{c.label}</div>
            <div className="text-sm font-bold text-gray-800 mt-0.5">{c.value}</div>
          </div>
        ))}
      </div>
    );
  };

  const SummaryTable = ({ data, nameLabel = 'Name', showId = false }) => {
    if (!data?.length) return null;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 text-left">
              {showId && <th className="p-2 border">ID</th>}
              <th className="p-2 border">{nameLabel}</th>
              <th className="p-2 border text-right">Spend</th>
              <th className="p-2 border text-right">Impressions</th>
              <th className="p-2 border text-right">Clicks</th>
              <th className="p-2 border text-right">CTR</th>
              <th className="p-2 border text-right">CPC</th>
              <th className="p-2 border text-right">Conversions</th>
              <th className="p-2 border text-right">Conv. Value</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={row.id || i} className="hover:bg-blue-50">
                {showId && <td className="p-2 border font-mono text-[10px]">{row.id}</td>}
                <td className="p-2 border font-medium max-w-[250px] truncate">{row.name}</td>
                <td className="p-2 border text-right text-red-600">{fmtMoney(row.spend)}</td>
                <td className="p-2 border text-right">{fmtNum(row.impressions)}</td>
                <td className="p-2 border text-right">{fmtNum(row.clicks)}</td>
                <td className="p-2 border text-right">{row.impressions > 0 ? fmtPct(row.clicks / row.impressions) : '-'}</td>
                <td className="p-2 border text-right">{row.clicks > 0 ? fmtMoney(row.spend / row.clicks) : '-'}</td>
                <td className="p-2 border text-right">{fmtNum(row.conversions)}</td>
                <td className="p-2 border text-right text-green-600">{fmtMoney(row.convValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <img src="/pinterestlogopng.png" alt="Pinterest" className="w-8 h-8 object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Pinterest API Explorer</h1>
            <p className="text-xs text-gray-500">Campaign, Ad Group & Ad seviyesinde tüm metrikleri test edin</p>
          </div>
        </div>
        {conn?.connected && (
          <div className="flex items-center gap-2 text-sm bg-green-50 text-green-700 px-3 py-1.5 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            {conn.accountName} — {conn.adAccountId}
          </div>
        )}
      </div>

      {/* Date Range + Load All */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4 flex items-center gap-4 flex-wrap">
        <label className="text-sm text-gray-600 font-medium">Date Range:</label>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" />
        <span className="text-gray-400">→</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" />
        <div className="flex-1" />
        <Btn onClick={loadAll} loading={Object.values(loading).some(Boolean)} variant="green">
          Load Everything
        </Btn>
      </div>

      {/* 1. Account-level Analytics */}
      <Section title="Account-Level Analytics" badge="Ad Account Daily">
        <div className="mb-3"><Btn onClick={loadAccountAnalytics} loading={loading.accountAnalytics}>Fetch Account Analytics</Btn></div>
        {accountAnalytics && (
          <>
            <KpiCards data={aggregateRows(accountAnalytics)} />
            {Array.isArray(accountAnalytics) && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="bg-gray-50">
                    <th className="p-2 border text-left">Date</th>
                    <th className="p-2 border text-right">Spend</th>
                    <th className="p-2 border text-right">Impressions</th>
                    <th className="p-2 border text-right">Clicks</th>
                    <th className="p-2 border text-right">CTR</th>
                    <th className="p-2 border text-right">CPC</th>
                    <th className="p-2 border text-right">CPM</th>
                    <th className="p-2 border text-right">Conversions</th>
                    <th className="p-2 border text-right">Conv. Value</th>
                    <th className="p-2 border text-right">Conv. Qty</th>
                  </tr></thead>
                  <tbody>
                    {[...accountAnalytics].sort((a, b) => (b.DATE || '').localeCompare(a.DATE || '')).map((row, i) => (
                      <tr key={i} className="hover:bg-blue-50">
                        <td className="p-2 border">{row.DATE}</td>
                        <td className="p-2 border text-right text-red-600">{fmtMoney(row.SPEND_IN_DOLLAR)}</td>
                        <td className="p-2 border text-right">{fmtNum(row.IMPRESSION_1)}</td>
                        <td className="p-2 border text-right">{fmtNum(row.CLICKTHROUGH_1)}</td>
                        <td className="p-2 border text-right">{fmtPct(row.CTR)}</td>
                        <td className="p-2 border text-right">{fmtMoney(row.CPC_IN_DOLLAR)}</td>
                        <td className="p-2 border text-right">{fmtMoney(row.CPM_IN_DOLLAR)}</td>
                        <td className="p-2 border text-right">{fmtNum(row.TOTAL_CONVERSIONS)}</td>
                        <td className="p-2 border text-right text-green-600">{fmtMoney(row.TOTAL_CONVERSIONS_VALUE)}</td>
                        <td className="p-2 border text-right">{fmtNum(row.TOTAL_CONVERSIONS_QUANTITY)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <JsonBlock data={accountAnalytics} />
          </>
        )}
      </Section>

      {/* 2. Campaigns */}
      <Section title="Campaigns" badge={campaigns?.items ? `${campaigns.items.length} campaigns` : null}>
        <div className="mb-3 flex gap-2">
          <Btn onClick={loadCampaigns} loading={loading.campaigns}>List Campaigns</Btn>
          <Btn onClick={loadCampaignAnalytics} loading={loading.campaignAnalytics}>Fetch Campaign Analytics</Btn>
        </div>
        {campaigns?.items && (
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-gray-50">
                <th className="p-2 border text-left">ID</th>
                <th className="p-2 border text-left">Name</th>
                <th className="p-2 border text-left">Status</th>
                <th className="p-2 border text-left">Objective</th>
                <th className="p-2 border text-right">Daily Budget</th>
                <th className="p-2 border text-right">Lifetime Budget</th>
              </tr></thead>
              <tbody>{campaigns.items.map(c => (
                <tr key={c.id} className="hover:bg-blue-50">
                  <td className="p-2 border font-mono text-[10px]">{c.id}</td>
                  <td className="p-2 border font-medium">{c.name}</td>
                  <td className="p-2 border">
                    <span className={`px-2 py-0.5 rounded text-xs ${c.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : c.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                  </td>
                  <td className="p-2 border">{c.objective_type}</td>
                  <td className="p-2 border text-right">{c.daily_spend_cap ? `$${(c.daily_spend_cap / 1000000).toFixed(0)}` : '-'}</td>
                  <td className="p-2 border text-right">{c.lifetime_spend_cap ? `$${(c.lifetime_spend_cap / 1000000).toFixed(0)}` : '-'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {campaignSummary.length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Campaign Performance Summary</h3>
            <SummaryTable data={campaignSummary} nameLabel="Campaign" />
          </>
        )}
        {campaignAnalytics && <JsonBlock data={campaignAnalytics} />}
      </Section>

      {/* 3. Ad Groups */}
      <Section title="Ad Groups" badge={adGroups?.items ? `${adGroups.items.length} ad groups` : null}>
        <div className="mb-3 flex gap-2">
          <Btn onClick={loadAdGroups} loading={loading.adGroups}>List Ad Groups</Btn>
          <Btn onClick={loadAdGroupAnalytics} loading={loading.adGroupAnalytics}>Fetch Ad Group Analytics</Btn>
        </div>
        {adGroups?.items && (
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-gray-50">
                <th className="p-2 border text-left">ID</th>
                <th className="p-2 border text-left">Name</th>
                <th className="p-2 border text-left">Status</th>
                <th className="p-2 border text-left">Campaign ID</th>
                <th className="p-2 border text-right">Bid (µ$)</th>
              </tr></thead>
              <tbody>{adGroups.items.map(g => (
                <tr key={g.id} className="hover:bg-blue-50">
                  <td className="p-2 border font-mono text-[10px]">{g.id}</td>
                  <td className="p-2 border font-medium">{g.name}</td>
                  <td className="p-2 border">
                    <span className={`px-2 py-0.5 rounded text-xs ${g.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : g.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{g.status}</span>
                  </td>
                  <td className="p-2 border font-mono text-[10px]">{g.campaign_id}</td>
                  <td className="p-2 border text-right">{g.bid_in_micro_currency || '-'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {adGroupSummary.length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Ad Group Performance Summary</h3>
            <SummaryTable data={adGroupSummary} nameLabel="Ad Group" />
          </>
        )}
        {adGroupAnalytics && <JsonBlock data={adGroupAnalytics} />}
      </Section>

      {/* 4. Ads */}
      <Section title="Ads (Pins)" badge={ads?.items ? `${ads.items.length} ads` : null}>
        <div className="mb-3 flex gap-2">
          <Btn onClick={loadAds} loading={loading.ads}>List Ads</Btn>
          <Btn onClick={loadAdAnalytics} loading={loading.adAnalytics}>Fetch Ad Analytics</Btn>
        </div>
        {ads?.items && (
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-gray-50">
                <th className="p-2 border text-left">ID</th>
                <th className="p-2 border text-left">Name</th>
                <th className="p-2 border text-left">Status</th>
                <th className="p-2 border text-left">Campaign</th>
                <th className="p-2 border text-left">Ad Group</th>
                <th className="p-2 border text-left">Pin ID</th>
              </tr></thead>
              <tbody>{ads.items.slice(0, 50).map(a => (
                <tr key={a.id} className="hover:bg-blue-50">
                  <td className="p-2 border font-mono text-[10px]">{a.id}</td>
                  <td className="p-2 border font-medium max-w-[200px] truncate">{a.name || '-'}</td>
                  <td className="p-2 border">
                    <span className={`px-2 py-0.5 rounded text-xs ${a.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : a.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                  </td>
                  <td className="p-2 border font-mono text-[10px]">{a.campaign_id}</td>
                  <td className="p-2 border font-mono text-[10px]">{a.ad_group_id}</td>
                  <td className="p-2 border font-mono text-[10px]">{a.pin_id || '-'}</td>
                </tr>
              ))}</tbody>
            </table>
            {ads.items.length > 50 && <p className="text-xs text-gray-400 mt-1">Showing 50 of {ads.items.length} ads</p>}
          </div>
        )}
        {adSummary.length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Ad Performance Summary</h3>
            <SummaryTable data={adSummary} nameLabel="Ad Name" showId />
          </>
        )}
        {adAnalytics && <JsonBlock data={adAnalytics} />}
      </Section>

      {/* 5. Custom API Explorer */}
      <Section title="Custom API Explorer">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <span className="text-xs text-gray-400 font-mono">https://api.pinterest.com/v5</span>
          <input
            type="text"
            value={customEndpoint}
            onChange={e => setCustomEndpoint(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadCustom()}
            placeholder="/user_account  or  /ad_accounts/549.../campaigns"
            className="flex-1 min-w-[300px] border rounded-lg px-3 py-1.5 text-sm font-mono"
          />
          <Btn onClick={loadCustom} loading={loading.custom}>Send</Btn>
        </div>
        {customResult && (
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-auto max-h-[500px] whitespace-pre-wrap">
            {JSON.stringify(customResult, null, 2)}
          </pre>
        )}
      </Section>
    </div>
  );
}
